import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  DatePicker,
  message,
  Col,
  Row,
  Tooltip,
} from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

function AddPatients() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const userType = useSelector((state) => state.user?.user?.roleDetails?.id);
  const userID = useSelector((state) => state.user.user?.id);
  const { TextArea } = Input;
  const API_URL = import.meta.env.VITE_API_URL;
  const accessToken = useSelector((state) => state.user.accessToken);
  const pharmacyDetails = useSelector((state) => state.user.pharmacyDetails);
  const [pharmacyList, setPharmacyList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [branchId, setBranchId] = useState();
  const [isBranchDisabled, setIsBranchDisabled] = useState(true);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Track screen width changes
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handlePharmacyChange = async (value) => {
    setIsBranchDisabled(false);
    setBranchList([]);
    setBranchId(null);
    form.setFieldsValue({ branch: undefined });

    try {
      // Special handling for pharmacist (userType 2)
      if (userType === 2) {
        // Fetch user data to get branch information
        const userResponse = await axios.get(
          `${API_URL}users?id=${userID}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        // Get active branches from user data
        const userData = userResponse.data.user;
        if (userData && userData.branches) {
          // Filter branches by active status AND matching pharmacy ID if pharmacyDetails exists
          const activeBranches = userData.branches.filter(branch =>
            branch.active &&
            (!pharmacyDetails || branch.PharmacyDetails?.id === pharmacyDetails.id)
          );
          setBranchList(activeBranches);

          // Auto-select the first branch if available
          if (activeBranches.length > 0) {
            const firstBranchId = activeBranches[0].id;
            setBranchId(firstBranchId);
            form.setFieldsValue({ branch: firstBranchId });
          }
        }
        return;
      }

      // Handle pharmacy admin (userType 4)
      if (userType === 4) {
        const pharmacyID = pharmacyDetails.id;
        const responseBranches = await axios.get(
          `${API_URL}pharmacies?id=${pharmacyID}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        let pharmacyData = responseBranches.data.Pharmacy;
        if (!pharmacyData.active) {
          setBranchList([]);
          return;
        }

        const activeBranches = pharmacyData.BranchesDetails.filter(
          (branch) => branch.active
        );
        setBranchList(activeBranches);
        return;
      }

      // Handle regular admin (userType 1 or other)
      if (value) {
        const selectedPharmacy = pharmacyList.find((ph) => ph.id === value);
        const responseBranches = await axios.get(
          `${API_URL}pharmacies?id=${selectedPharmacy.id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        let pharmacyData = responseBranches.data.Pharmacy;
        if (!pharmacyData.active) {
          setBranchList([]);
          return;
        }

        const activeBranches = pharmacyData.BranchesDetails.filter(
          (branch) => branch.active
        );
        setBranchList(activeBranches);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      message.error("Failed to load branch data.");
    }
  };

  const handleQuickAdd = async () => {
    try {
      // Validate only firstName, contact, pharmacy and branch
      await form.validateFields(['firstName', 'contact', 'pharmacy', 'branch']);

      const values = form.getFieldsValue();

      // Check if branch is selected
      if (!values.branch) {
        message.error("Please select a branch before submitting");
        return;
      }

      // Create basic payload with only the mandatory fields
      const basicPayload = {
        role: 3,
        firstName: values.firstName.trim(),
        mobileNumber: values.contact.toString(),
      };

      // Create user with minimal information
      const response = await axios.post(`${API_URL}users`, basicPayload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Add patient to branch
      await axios.post(
        `${API_URL}Branches/addOnePatient`,
        {
          branchId: values.branch,
          userId: response.data.createdUser.id,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      message.success("Patient quickly added successfully");
      navigate(-1);
      form.resetFields();
    } catch (error) {
      console.error("Error quick-adding patient:", error);
      if (error.errorFields) {
        // Form validation error
        message.error("Please fill in all required fields");
      } else {
        // API error
        message.error(`${error.response?.data?.message || "Failed to add patient"}`);
      }
    }
  };

  const handleSubmit = async (values) => {
    // Check if pharmacy and branch are selected
    if (!values.branch) {
      message.error("Please select a branch before submitting");
      return;
    }

    // Create basic payload with mandatory fields
    const basicPayload = {
      role: 3,
      firstName: values.firstName.trim(),
      mobileNumber: values.contact.toString(),
    };

    // Add additional fields if they exist
    if (values.gender) {
      basicPayload.gender = values.gender.toLowerCase();
    }

    if (values.Date_of_birth) {
      basicPayload.dob = values.Date_of_birth instanceof Date
        ? values.Date_of_birth.toISOString().split("T")[0]
        : values.Date_of_birth;
    }

    if (values.Height) {
      basicPayload.Height = values.Height;
    }

    if (values.Weight) {
      basicPayload.Weight = values.Weight;
    }

    if (values.MedicalHistory) {
      basicPayload.medicalHistory = values.MedicalHistory.trim();
    }

    if (values.allergies) {
      basicPayload.allergies = values.allergies.trim();
    }

    try {
      // Create user with either basic or full details
      const response = await axios.post(`${API_URL}users`, basicPayload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // Add patient to branch
      const addBranch = await axios.post(
        `${API_URL}Branches/addOnePatient`,
        {
          branchId: values.branch,
          userId: response.data.createdUser.id,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      message.success("Patient created successfully");
      navigate(-1);
      form.resetFields();
    } catch (error) {
      console.error("Error Create Patients:", error);
      message.error(`${error.response?.data?.message || "Failed to create patient"}`);
    }
  };

  useEffect(() => {
    if (userType === 1) {
      const fetchPharmacies = async () => {
        try {
          const pharmacyResponse = await axios.get(
            `${API_URL}pharmacies/get-pharmacies?limit=1000`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          const activePharmacies = pharmacyResponse.data.Pharmacies.filter(
            (pharmacy) => pharmacy.active
          ).map((pharmacy) => ({
            ...pharmacy,
            BranchesDetails:
              pharmacy.BranchesDetails?.filter((branch) => branch.active) || [],
          }));

          setPharmacyList(activePharmacies);
        } catch (error) {
          console.error("Error fetching pharmacies:", error);
        }
      };
      fetchPharmacies();
    }
  }, [userType]);

  useEffect(() => {
    if ((userType === 4) && pharmacyDetails) {
      form.setFieldsValue({
        pharmacy: pharmacyDetails.name,
      });
    }
  }, [pharmacyDetails]);

  useEffect(() => {
    // Separate handling for different user types
    if (userType === 2) {
      // For pharmacists, call handlePharmacyChange without parameters
      handlePharmacyChange();
      if (pharmacyDetails) {
        form.setFieldsValue({
          pharmacy: pharmacyDetails.name,
        });
      }
    } else if (userType === 4 && pharmacyDetails) {
      // For pharmacy admins, call handlePharmacyChange
      handlePharmacyChange();
    }
  }, [userType, pharmacyDetails]);

  useEffect(() => {
    // Set phone number if passed from Patients component
    if (location.state?.phoneNumber) {
      form.setFieldsValue({
        contact: location.state.phoneNumber
      });
    }
  }, [location.state]);

  const handleCancel = () => {
    form.resetFields();
    navigate(-1);
  };

  // Dynamic form item layout
  const formItemLayout = screenWidth < 768 ? { span: 24 } : { span: 12 };

  return (
    <div className="px-2 sm:px-4 md:px-6 lg:px-8">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ width: "100%" }}
      >
        <div className="flex flex-col space-y-4">
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold mb-2 md:mb-4">Add New Patient</p>
          <div>
            <div
              className="flex flex-col bg-white p-4 sm:p-6 md:p-8 lg:p-10 rounded-sm"
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        First Name
                      </p>
                    }
                    name="firstName"
                    rules={[
                      { required: true, message: "Please Enter First Name" },
                      {
                        whitespace: true,
                        message: "No white Space",
                      },
                    ]}
                  >
                    <Input style={{ height: "40px" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Contact Number
                      </p>
                    }
                    name="contact"
                    rules={[
                      { required: true, message: "Please Enter Contact number" },
                      {
                        pattern: /^[0-9]{11}$/,
                        message: "Contact number must be exactly 11 digits",
                      },
                    ]}
                  >
                    <Input
                      className="no-arrows"
                      inputMode="numeric"
                      maxLength={11}
                      style={{
                        width: "100%",
                        height: "40px",
                        padding: "0",
                        lineHeight: "40px",
                        paddingLeft: "15px",
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                {userType === 1 ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <p className="text-sm md:text-base font-medium">
                          Pharmacy
                        </p>
                      }
                      name="pharmacy"
                      rules={[
                        { required: true, message: "Please select a pharmacy" },
                      ]}
                    >
                      <Select
                        style={{ height: "40px" }}
                        onChange={handlePharmacyChange}
                      >
                        {pharmacyList.map((pharmacy) => (
                          <Select.Option key={pharmacy.id} value={pharmacy.id}>
                            {pharmacy.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                ) : userType === 2 ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <p className="text-sm md:text-base font-medium">
                          Pharmacy
                        </p>
                      }
                      name="pharmacy"
                    >
                      {branchList && branchList.length > 0 ? (
                        <Input
                          value={branchList[0]?.PharmacyDetails?.name}
                          disabled
                          style={{ height: "40px", color: "#232323" }}
                        />
                      ) : (
                        <Input
                          value="Loading pharmacy..."
                          disabled
                          style={{ height: "40px", color: "#232323" }}
                        />
                      )}
                    </Form.Item>
                  </Col>
                ) : userType === 4 ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <p className="text-sm md:text-base font-medium">
                          Pharmacy
                        </p>
                      }
                      name="pharmacy"
                    >
                      {pharmacyDetails ? (
                        <Input
                          value={pharmacyDetails.name}
                          disabled
                          style={{ height: "40px", color: "#232323" }}
                        />
                      ) : (
                        <p>No pharmacy details available</p>
                      )}
                    </Form.Item>
                  </Col>
                ) : null}

                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Branch
                      </p>
                    }
                    name="branch"
                    rules={[{ required: true, message: "Please select a branch" }]}
                  >
                    <Select
                      style={{ height: "40px" }}
                      disabled={isBranchDisabled}
                      value={branchId}
                      onChange={(value) => setBranchId(value)}
                    >
                      {branchList.length > 0 ? (
                        branchList.map((branch) => (
                          <Select.Option key={branch.id} value={branch.id}>
                            {branch.name}
                          </Select.Option>
                        ))
                      ) : (
                        <Select.Option disabled>
                          No branches available
                        </Select.Option>
                      )}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Quick Add Button - Only for First Name and Contact Number */}
              <div className="flex justify-end mb-4">
                <Tooltip title="This will only save the patient with First Name and Contact Number">
                  <Button
                    type="primary"
                    onClick={handleQuickAdd}
                    className="h-auto py-2 px-3 sm:py-3 sm:px-4 md:py-4 md:px-8"
                  >
                    Quick Add Patient
                  </Button>
                </Tooltip>
              </div>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Last Name
                      </p>
                    }
                    name="lastName"
                    rules={[
                      { required: false },
                      {
                        whitespace: true,
                        message: "No white Space",
                      },
                    ]}
                  >
                    <Input style={{ height: "40px" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Gender
                      </p>
                    }
                    name="gender"
                    rules={[{ required: false }]}
                  >
                    <Select style={{ height: "40px" }}>
                      <Select.Option value="male">Male</Select.Option>
                      <Select.Option value="female">Female</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Date of Birth
                      </p>
                    }
                    name="Date_of_birth"
                    rules={[{ required: false }]}
                  >
                    <DatePicker
                      prefix={<img src="/assets/images/date.svg" alt="calendar icon" />}
                      suffixIcon={null}
                      placeholder=""
                      style={{ width: "100%", height: "40px" }}
                      disabledDate={(current) =>
                        current && current >= moment().startOf("day")
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Weight
                      </p>
                    }
                    name="Weight"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      className="no-arrows height-40px input-height-full"
                      style={{
                        width: "100%",
                        height: "40px",
                        padding: "0",
                        lineHeight: "40px",
                        textAlign: "center",
                      }}
                      addonAfter="kg"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Height
                      </p>
                    }
                    name="Height"
                    style={{ height: "40px" }}
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      className="no-arrows height-40px input-height-full"
                      style={{
                        width: "100%",
                        height: "40px",
                        padding: "0",
                        lineHeight: "40px",
                        textAlign: "center",
                      }}
                      addonAfter="cm"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Medical History
                      </p>
                    }
                    name="MedicalHistory"
                    rules={[{ required: false }]}
                  >
                    <TextArea rows={4} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <p className="text-sm md:text-base font-medium">
                        Allergies
                      </p>
                    }
                    name="allergies"
                    rules={[{ required: false }]}
                  >
                    <TextArea rows={4} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className="flex flex-col sm:flex-row justify-between mt-6 gap-4">
              <Button
                onClick={handleCancel}
                className="h-auto py-2 px-3 sm:py-3 sm:px-4 md:py-4 md:px-8"
                style={{ backgroundColor: "#F8F8F8" }}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                htmlType="submit"
                className="h-auto py-2 px-3 sm:py-3 sm:px-4 md:py-4 md:px-8"
              >
                Add Patient
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default AddPatients;
